import { sortWork, URGENCY_RANK } from '../tasks/deriveTasks';
import { ClientTask, ClientWork, SourceKey, SourceStatuses, TaskKind, Urgency } from '../tasks/types';
import { BAND_LABELS, TONES } from '../ui';

/**
 * Success updates fall due every six weeks for every active client, so in a
 * team view they outnumber everything else. They get their own segment;
 * "Milestones" holds the deadlines of the programme itself.
 */
export type InboxSegment = 'programme' | 'success';

export const INBOX_SEGMENTS: { key: InboxSegment; label: string }[] = [
  { key: 'programme', label: 'Milestones' },
  { key: 'success', label: 'Success updates' },
];

export type InboxFilter = 'all' | 'overdue' | Exclude<TaskKind, 'success'>;

/** Filters within the programme segment; the success segment is grouped instead. */
export const INBOX_FILTERS: { key: InboxFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'report', label: 'Reports' },
  { key: 'assessment', label: 'Assessments' },
  { key: 'session', label: 'Sessions to book' },
  { key: 'checkin', label: 'Check-ins' },
  { key: 'renewal', label: 'Renewals' },
];

/** The reads each kind of task depends on, so a count can say "unknown" instead of a confident 0. */
const KIND_SOURCES: Record<TaskKind, SourceKey[]> = {
  report: ['cleared', 'reports', 'submissions'],
  assessment: ['cleared', 'submissions'],
  session: ['cleared'],
  checkin: ['checkIns'],
  success: ['success'],
  renewal: [],
};

const PROGRAMME_SOURCES: SourceKey[] = ['cleared', 'reports', 'submissions', 'checkIns'];

export function filterSourceState(filter: InboxFilter, sources: SourceStatuses): 'ready' | 'loading' | 'error' {
  return sourceState(filter === 'all' || filter === 'overdue' ? PROGRAMME_SOURCES : KIND_SOURCES[filter], sources);
}

export function segmentSourceState(segment: InboxSegment, sources: SourceStatuses): 'ready' | 'loading' | 'error' {
  return sourceState(segment === 'success' ? KIND_SOURCES.success : PROGRAMME_SOURCES, sources);
}

function sourceState(keys: SourceKey[], sources: SourceStatuses): 'ready' | 'loading' | 'error' {
  if (keys.some((key) => sources[key] === 'error')) return 'error';
  if (keys.some((key) => sources[key] === 'loading')) return 'loading';
  return 'ready';
}

const worstUrgency = (tasks: ClientTask[]) =>
  tasks.reduce<Urgency>((worst, task) => (URGENCY_RANK[task.urgency] < URGENCY_RANK[worst] ? task.urgency : worst), 'upcoming');

/** Keeps only the tasks `keep` accepts, re-ranks each client and drops clients with nothing left. */
function narrowTasks(work: ClientWork[], keep: (task: ClientTask) => boolean): ClientWork[] {
  return work.flatMap((item) => {
    const tasks = item.tasks.filter(keep);
    if (tasks.length === 0) return [];
    return tasks.length === item.tasks.length ? [item] : [{ ...item, tasks, urgency: worstUrgency(tasks) }];
  });
}

/** Takes out tasks being cleared. */
export function withoutHidden(work: ClientWork[], hidden: Set<string>): ClientWork[] {
  return hidden.size === 0 ? work : narrowTasks(work, (task) => !hidden.has(task.id));
}

export const inSegment = (task: ClientTask, segment: InboxSegment) => (segment === 'success' ? task.kind === 'success' : task.kind !== 'success');

/**
 * The clients with work in one segment, each carrying only that segment's tasks, re-sorted on those
 * tasks alone: a client's place in the success list depends on their success update, not an old report.
 */
export const segmentWork = (work: ClientWork[], segment: InboxSegment) => sortWork(narrowTasks(work, (task) => inSegment(task, segment)));

/** Lead-coach key used by the coach filter; clients without a lead coach share one. */
export const NO_COACH = '__none';
export const coachKey = (item: ClientWork) => item.client.coachId ?? NO_COACH;

export interface CoachOption {
  key: string;
  name: string;
  count: number;
}

/** Every lead coach with clients in `work`, by name, with how many clients each has there. */
export function coachOptions(work: ClientWork[]): CoachOption[] {
  const byKey = new Map<string, CoachOption>();
  work.forEach((item) => {
    const key = coachKey(item);
    const entry = byKey.get(key) ?? { key, name: key === NO_COACH ? 'No lead coach' : item.client.coachName, count: 0 };
    entry.count += 1;
    byKey.set(key, entry);
  });
  return Array.from(byKey.values()).sort((a, b) => (a.key === NO_COACH ? 1 : b.key === NO_COACH ? -1 : a.name.localeCompare(b.name)));
}

export const filterByCoach = (work: ClientWork[], coach: string | null) => (coach ? work.filter((item) => coachKey(item) === coach) : work);

const matchesFilter = (item: ClientWork, filter: InboxFilter) =>
  filter === 'all' ? true : filter === 'overdue' ? item.urgency === 'overdue' : item.tasks.some((task) => task.kind === filter);

export function filterWork(work: ClientWork[], filter: InboxFilter, search: string): ClientWork[] {
  const needle = search.trim().toLowerCase();
  return work.filter((item) => matchesFilter(item, filter) && (!needle || item.client.name.toLowerCase().includes(needle)));
}

/** How many clients each filter would show. */
export function filterCounts(work: ClientWork[]): Record<InboxFilter, number> {
  return INBOX_FILTERS.reduce(
    (counts, { key }) => ({ ...counts, [key]: work.filter((item) => matchesFilter(item, key)).length }),
    {} as Record<InboxFilter, number>
  );
}

export interface InboxGroup {
  key: string;
  label: string;
  color: string;
  items: ClientWork[];
}

export function groupByUrgency(work: ClientWork[]): { urgency: Urgency; items: ClientWork[] }[] {
  return (['overdue', 'soon', 'upcoming'] as Urgency[])
    .map((urgency) => ({ urgency, items: work.filter((item) => item.urgency === urgency) }))
    .filter((group) => group.items.length > 0);
}

/**
 * Milestones are banded by urgency. Success updates are split by whether
 * the client has ever had one: an overdue repeat update is the manager's
 * concern first, a first update for a newer client second.
 */
export function inboxGroups(work: ClientWork[], segment: InboxSegment): InboxGroup[] {
  if (segment === 'programme') {
    return groupByUrgency(work).map(({ urgency, items }) => ({ key: urgency, label: BAND_LABELS[urgency], color: TONES[urgency].color, items }));
  }
  const first = (item: ClientWork) => item.tasks.every((task) => task.awaitingFirst);
  return [
    { key: 'repeat', label: 'Overdue', color: TONES.overdue.color, items: work.filter((item) => !first(item)) },
    { key: 'first', label: 'First update due', color: TONES.soon.color, items: work.filter(first) },
  ].filter((group) => group.items.length > 0);
}

const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** A few words per task for the list rows; the detail panel carries the full sentence. */
export function chipLabel(task: ClientTask): string {
  switch (task.kind) {
    case 'report':
      return task.title.includes('initial') ? 'Initial report' : 'Review report';
    case 'session':
      return task.title.replace(/^Book an? /, 'Book ').replace(/ (session|screening)$/, '');
    case 'assessment':
      return capitalise(task.title.replace(/^Submit the /, '').replace(/ assessment$/, ''));
    case 'checkin':
      return task.checkInType === '10_week' ? '10-week check-in' : '2-week check-in';
    case 'success':
      return task.awaitingFirst ? 'First success update' : 'Success update';
    case 'renewal':
      return 'Renewal talk';
  }
}
